import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppLayout } from '../components/layout';
import { Button } from '@/components/ui/button';
import { Plus, ArrowLeft, Loader2, FileText, Folder, ChevronDown, BookOpen, ExternalLink, Settings, BarChart3 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  getHelpCenter,
  getHelpCenterStructure,
  createCategory,
  updateCategory,
  deleteCategory,
  reorderCategories,
  createItem,
  deleteItem,
  reorderItems,
  updateHelpCenterSettings,
} from '../services/api';
import type { HelpCenter, HelpCenterStructureNode, CreateCategoryRequest, UpdateCategoryRequest } from '../types';
import { StructureTree } from '../components/features/help-center/structure/StructureTree';
import { CategoryModal } from '../components/features/help-center/CategoryModal';
import { AddGuideModal } from '../components/features/help-center/AddGuideModal';
import { EditGuideItemModal } from '../components/features/help-center/EditGuideItemModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useUIStore } from '../store/uiStore';
import { useBillingInfo, useIsProCloudPlan, useUserStore } from '../store/userStore';
import { useOpenHelpCenter } from '../hooks/useOpenHelpCenter';
import UpsellModal from '../components/modals/UpsellModal';
import { HelpCenterSettingsDrawer } from '../components/features/help-center/HelpCenterSettingsDrawer';
import { HomepagePinsSection } from '../components/features/help-center/HomepagePinsSection';
import { LimitReachedAlert } from '../components/features/portal/LimitReachedAlert';
import {
  getNodePinRef,
  isPinned,
  pinRefKey,
  removePinsForDeletedNode,
} from '../libs/homepage-pins-utils';
import type { HomepagePinRef } from '../types';
import { useTranslation } from 'react-i18next';

const EditHelpCenterStructurePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal, closeModal, modals } = useUIStore();
  const isProCloudPlan = useIsProCloudPlan();
  const billingInfo = useBillingInfo();
  const { account } = useUserStore();
  const { openHelpCenter, openExternalUrl } = useOpenHelpCenter();
  const { t } = useTranslation();

  const [helpCenter, setHelpCenter] = useState<HelpCenter | null>(null);
  const [structure, setStructure] = useState<HelpCenterStructureNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Store expanded category IDs to preserve across structure updates
  const expandedCategoryIdsRef = useRef<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any | null>(null);
  const [parentIdForNew, setParentIdForNew] = useState<number | null>(null);
  const [nodeToDelete, setNodeToDelete] = useState<HelpCenterStructureNode | null>(null);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [showAddGuideModal, setShowAddGuideModal] = useState(false);
  const [editingGuideItem, setEditingGuideItem] = useState<{ helpCenterId: string; itemId: string } | null>(null);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The article editor redirects guide items here with `editGuideItemId` in
  // location state; open the guide editor modal for that item once, then clear
  // the state so a reload does not reopen it.
  useEffect(() => {
    const editGuideItemId = (location.state as { editGuideItemId?: string | number } | null)?.editGuideItemId;
    if (!id || editGuideItemId === undefined || editGuideItemId === null) return;
    setEditingGuideItem({ helpCenterId: id, itemId: String(editGuideItemId) });
    navigate(location.pathname, { replace: true, state: null });
  }, [id, location.state, location.pathname, navigate]);

  const fetchData = async (showLoading = true) => {
    if (!id) return;

    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const [helpCenterResponse, structureResponse] = await Promise.all([
        getHelpCenter(id),
        getHelpCenterStructure(id),
      ]);

      if (helpCenterResponse.ok && helpCenterResponse.helpCenter) {
        setHelpCenter(helpCenterResponse.helpCenter);
      } else {
        setError(t('editStructure.loadHelpCenterFailed'));
      }

      if (structureResponse.ok && structureResponse.structure) {
        setStructure(structureResponse.structure);
        // Expanded state will be preserved by StructureTree component via ref
      } else if (structureResponse.ok) {
        setStructure([]);
      } else {
        setError(t('editStructure.loadStructureFailed'));
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(t('editStructure.loadDataFailed'));
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleAddCategory = (parentId: number | null) => {
    setEditingCategory(null);
    setParentIdForNew(parentId);
    setCategoryError(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setParentIdForNew(null);
    setCategoryError(null);
    setShowCategoryModal(true);
  };

  const handleDeleteNode = (node: HelpCenterStructureNode) => {
    setNodeToDelete(node);
    openModal('delete');
  };

  const homepagePins = helpCenter?.settings?.homepage?.pins || [];

  const saveHomepagePins = async (nextPins: HomepagePinRef[]) => {
    if (!id || !helpCenter) return;

    try {
      const response = await updateHelpCenterSettings(
        id,
        { homepage: { pins: nextPins } },
        undefined,
        false
      );

      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
      } else {
        throw new Error(response.error || t('helpCenter.highlights.updateFailed'));
      }
    } catch (error) {
      console.error('Error saving highlights:', error);
      toast.error(t('helpCenter.highlights.updateFailed'));
    }
  };

  const handleTogglePin = async (node: HelpCenterStructureNode) => {
    if (!helpCenter) return;

    const ref = getNodePinRef(node);
    if (!ref) return;

    const currentPins = helpCenter.settings?.homepage?.pins || [];
    const nextPins = isPinned(currentPins, ref)
      ? currentPins.filter((pin) => pinRefKey(pin) !== pinRefKey(ref))
      : [...currentPins, ref];

    await saveHomepagePins(nextPins);
    toast.success(
      isPinned(currentPins, ref)
        ? t('helpCenter.highlights.removed')
        : t('helpCenter.highlights.added')
    );
  };

  const handleConfirmDelete = async () => {
    if (!nodeToDelete || !id) return;

    closeModal('delete');
    setIsSubmitting(true);

    try {
      const deleteId =
        nodeToDelete.type === 'category'
          ? nodeToDelete.category!.id.toString()
          : nodeToDelete.item!.id.toString();

      let response;
      if (nodeToDelete.type === 'category') {
        response = await deleteCategory(id, deleteId);
      } else {
        response = await deleteItem(id, deleteId);
      }

      if (response.ok) {
        const currentPins = helpCenter?.settings?.homepage?.pins || [];
        const nextPins = removePinsForDeletedNode(currentPins, nodeToDelete);
        if (nextPins.length !== currentPins.length) {
          await saveHomepagePins(nextPins);
        }

        toast.success(nodeToDelete.type === 'category' ? t('editStructure.categoryDeleted') : t('editStructure.itemDeleted'));
        await fetchData();
        setNodeToDelete(null);
      } else {
        const errorMessage = response.errors && response.errors.length > 0
          ? response.errors[0]
          : response.error || (nodeToDelete.type === 'category' ? t('editStructure.deleteCategoryFailed') : t('editStructure.deleteItemFailed'));
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Error deleting:', err);
        toast.error(nodeToDelete?.type === 'category' ? t('editStructure.deleteCategoryFailed') : t('editStructure.deleteItemFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategorySubmit = async (data: CreateCategoryRequest | UpdateCategoryRequest) => {
    if (!id) return;

    setIsSubmitting(true);
    setCategoryError(null);

    try {
      let response;
      if (editingCategory) {
        response = await updateCategory(id, editingCategory.id.toString(), data as UpdateCategoryRequest);
      } else {
        response = await createCategory(id, data as CreateCategoryRequest);
      }

      if (response.ok && response.category) {
        toast.success(editingCategory ? t('editStructure.categoryUpdated') : t('editStructure.categoryCreated'));
        setShowCategoryModal(false);
        setEditingCategory(null);
        setParentIdForNew(null);
        await fetchData();
      } else {
        const errorMessage = response.errors && response.errors.length > 0
          ? response.errors[0]
          : response.error || `Failed to ${editingCategory ? 'update' : 'create'} category`;
        setCategoryError(errorMessage);
      }
    } catch (err) {
      console.error('Error saving category:', err);
      setCategoryError('Failed to save category. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReorderCategories = async (updates: Array<{ category_id: number; parent_id: number | null; order: number }>) => {
    if (!id) return;

    try {
      const response = await reorderCategories(id, updates);
      if (response.ok) {
        // Silently refetch in background - don't show loading state
        fetchData(false).catch(err => {
          console.error('Error refetching after reorder:', err);
        });
      } else {
        toast.error(t('editStructure.reorderCategoriesFailed'));
        // Refetch to restore correct state
        await fetchData();
      }
    } catch (err) {
      console.error('Error reordering categories:', err);
      toast.error(t('editStructure.reorderCategoriesFailed'));
      // Refetch to restore correct state
      await fetchData();
    }
  };

  const handleReorderItems = async (updates: Array<{ item_id: number; category_id: number | null; order: number }>) => {
    if (!id) return;

    try {
      const response = await reorderItems(id, updates);
      if (response.ok) {
        // Silently refetch in background - don't show loading state
        fetchData(false).catch(err => {
          console.error('Error refetching after reorder:', err);
        });
      } else {
        toast.error(t('editStructure.reorderItemsFailed'));
        // Refetch to restore correct state
        await fetchData();
      }
    } catch (err) {
      console.error('Error reordering items:', err);
      toast.error(t('editStructure.reorderItemsFailed'));
      // Refetch to restore correct state
      await fetchData();
    }
  };

  const handleAddArticle = async (categoryId: number | null = null) => {
    if (!id) return;

    // Check item limits before attempting to add
    if (!isProCloudPlan) {
      const itemsLimit = billingInfo?.helpCenterItemsLimit ?? 20;
      const itemsCreated = billingInfo?.helpCenterItemsCreated ?? 0;
      if (itemsCreated >= itemsLimit) {
        toast.error(t('editStructure.freePlanItemsLimit', { count: itemsLimit }));
        openModal('upsell');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Create item with article (item creation will create the article)
      const itemResponse = await createItem(id, {
        item_type: 'article',
        // Don't provide item_id - article will be created automatically
        category_id: categoryId ?? null,
        is_published: true,
        article_title: t('editStructure.untitledArticle'),
        article_description: '',
        article_content_text: '',
        article_settings: {
          showAuthor: true,
        },
      });

      if (!itemResponse.ok || !itemResponse.item) {
        const errorMsg = itemResponse.error || t('editStructure.createItemFailed');
        // If error mentions limit or upgrade, show upsell modal
        if (errorMsg.toLowerCase().includes('limit') || errorMsg.toLowerCase().includes('upgrade')) {
          openModal('upsell');
        }
        throw new Error(errorMsg);
      }

      const item = itemResponse.item;

      // Navigate to edit page using item ID
      navigate(`/help-centers/${id}/items/${item.id}/edit`);
    } catch (err) {
      console.error('Error creating article:', err);
      toast.error(err instanceof Error ? err.message : t('editStructure.createArticleFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditItem = (item: any) => {
    if (!id) return;
    
    if (item.item_type === 'article') {
      // Navigate to article editor
      navigate(`/help-centers/${id}/items/${item.id}/edit`);
    } else if (item.item_type === 'guide') {
      // Open guide item editor modal
      setEditingGuideItem({
        helpCenterId: id,
        itemId: item.id.toString(),
      });
    }
  };

  const [categoryIdForNewGuide, setCategoryIdForNewGuide] = useState<number | null>(null);

  const handleAddGuide = (categoryId: number | null = null) => {
    setCategoryIdForNewGuide(categoryId);
    setShowAddGuideModal(true);
  };

  const handleAddGuideToHelpCenter = async (guideIds: number[]) => {
    if (!id || guideIds.length === 0) return;

    // Check item limits before attempting to add
    if (!isProCloudPlan) {
      const itemsLimit = billingInfo?.helpCenterItemsLimit ?? 20;
      const itemsCreated = billingInfo?.helpCenterItemsCreated ?? 0;
      if (itemsCreated >= itemsLimit) {
        toast.error(t('editStructure.freePlanItemsLimit', { count: itemsLimit }));
        openModal('upsell');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const promises = guideIds.map(guideId =>
        createItem(id, {
          item_type: 'guide',
          item_id: guideId,
          category_id: categoryIdForNewGuide ?? null,
          is_published: true,
        })
      );

      const responses = await Promise.all(promises);
      const successful = responses.filter(r => r.ok && r.item);
      const failed = responses.filter(r => !r.ok);

      if (successful.length > 0) {
        toast.success(
          `Successfully added ${successful.length} guide${successful.length > 1 ? 's' : ''} to help center`
        );
        setShowAddGuideModal(false);
        setCategoryIdForNewGuide(null);
        await fetchData();
      }

      if (failed.length > 0) {
        const errorMessage = failed[0].errors && failed[0].errors.length > 0
          ? failed[0].errors[0]
          : failed[0].error || 'Failed to add some guides';
        toast.error(errorMessage);
        // If error mentions limit or upgrade, show upsell modal
        if (errorMessage.toLowerCase().includes('limit') || errorMessage.toLowerCase().includes('upgrade')) {
          openModal('upsell');
        }
      }
    } catch (err) {
      console.error('Error adding guides:', err);
      toast.error(t('editStructure.addGuidesFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpgradeClick = () => {
    const url =
      "https://folge.me/go/cloud?cloudemail=" +
      encodeURIComponent(account?.email || "") +
      "#pricing";
    openExternalUrl(url);
  };

  const breadcrumbs = [
    { title: t('dashboard.breadcrumb'), href: '/dashboard' },
    { title: t('sidebar.helpCenters'), href: '/help-centers' },
    { title: helpCenter?.title || t('editStructure.breadcrumb'), isCurrentPage: true },
  ];

  if (loading) {
    return (
      <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (error || !helpCenter) {
    return (
      <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
        <div className="p-4">
          <div className="text-destructive">{error || t('editStructure.notFound')}</div>
          <Button onClick={() => navigate('/help-centers')} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHelpCenters')}
          </Button>
        </div>
      </AppLayout>
    );
  }

  const itemsLimit = billingInfo?.helpCenterItemsLimit ?? 20;
  const itemsCreated = billingInfo?.helpCenterItemsCreated ?? 0;
  const isAtItemLimit = !isProCloudPlan && itemsCreated >= itemsLimit;

  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <UpsellModal
        isOpen={modals.upsell}
        onClose={() => closeModal("upsell")}
      />
      <div className="p-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-2xl font-bold">{helpCenter.title}             {helpCenter.slug && (
              <button
                onClick={() => openHelpCenter(helpCenter.slug)}
                className="text-muted-foreground hover:text-foreground transition-colors p-1"
                title={t('editStructure.openHelpCenter')}
              >
                <ExternalLink className="h-5 w-5" />
              </button>
            )}</h1>
              <p className="text-muted-foreground mt-1">{t('editStructure.subtitle')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/help-centers')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/reports?helpCenterId=${id}`)}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              {t('sidebar.reports')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSettingsDrawer(true)}
            >
              <Settings className="h-4 w-4 mr-2" />
              {t('common.settings')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button disabled={isAtItemLimit}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('common.add')}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleAddArticle(null)} disabled={isAtItemLimit}>
                  <FileText className="h-4 w-4 mr-2" />
                  {t('editStructure.addArticle')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAddGuide(null)} disabled={isAtItemLimit}>
                  <BookOpen className="h-4 w-4 mr-2" />
                  {t('editStructure.addGuide')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAddCategory(null)}>
                  <Folder className="h-4 w-4 mr-2" />
                  {t('editStructure.addCategory')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {!isProCloudPlan && (
          <div className="mb-4">
            <LimitReachedAlert
              message={`${t('editStructure.itemsCount', { created: itemsCreated, limit: itemsLimit })}${isAtItemLimit ? t('editStructure.limitReachedSuffix') : ''}`}
              onUpgradeClick={handleUpgradeClick}
            />
          </div>
        )}

        {helpCenter && (
          <HomepagePinsSection
            helpCenter={helpCenter}
            structure={structure}
            onHelpCenterUpdate={setHelpCenter}
          />
        )}

        <div className="bg-card border rounded-lg p-4 min-h-[500px]">
          {structure.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">{t('editStructure.noStructure')}</p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  <DropdownMenuItem onClick={() => handleAddArticle(null)}>
                    <FileText className="h-4 w-4 mr-2" />
                    Add Article
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddGuide(null)}>
                    <BookOpen className="h-4 w-4 mr-2" />
                    Add Guide
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddCategory(null)}>
                    <Folder className="h-4 w-4 mr-2" />
                    Add Category
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <StructureTree
              structure={structure}
              onReorderCategories={handleReorderCategories}
              onReorderItems={handleReorderItems}
              onAddCategory={handleAddCategory}
              onEditCategory={handleEditCategory}
              onDeleteNode={handleDeleteNode}
              onAddArticle={handleAddArticle}
              onAddGuide={handleAddGuide}
              onEditItem={handleEditItem}
              onTogglePin={handleTogglePin}
              pinnedPinKeys={new Set(homepagePins.map((pin) => pinRefKey(pin)))}
              helpCenterId={helpCenter.id}
              helpCenterSlug={helpCenter.slug}
              expandedCategoryIdsRef={expandedCategoryIdsRef}
            />
          )}
        </div>
      </div>

      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
          setParentIdForNew(null);
          setCategoryError(null);
        }}
        onSubmit={handleCategorySubmit}
        category={editingCategory}
        parentId={parentIdForNew}
        isSubmitting={isSubmitting}
        error={categoryError}
      />

      <AddGuideModal
        isOpen={showAddGuideModal}
        onClose={() => {
          setShowAddGuideModal(false);
          setCategoryIdForNewGuide(null);
        }}
        onAdd={handleAddGuideToHelpCenter}
        helpCenterId={helpCenter.id}
        categoryId={categoryIdForNewGuide}
        isSubmitting={isSubmitting}
      />

      {editingGuideItem && (
        <EditGuideItemModal
          isOpen={!!editingGuideItem}
          onClose={() => setEditingGuideItem(null)}
          helpCenterId={editingGuideItem.helpCenterId}
          itemId={editingGuideItem.itemId}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      <Dialog open={modals.delete} onOpenChange={(open) => {
        if (!open) {
          closeModal('delete');
          setNodeToDelete(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {nodeToDelete?.type === 'category' ? t('editStructure.deleteCategory') : t('editStructure.deleteItem')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {t('editStructure.deleteConfirm', {
                title: nodeToDelete?.type === 'category'
                  ? nodeToDelete.category?.title
                  : nodeToDelete?.item?.article?.title || nodeToDelete?.item?.guide?.title || t('editStructure.thisItem'),
              })}
              {nodeToDelete?.type === 'category' && nodeToDelete.children && nodeToDelete.children.length > 0 && (
                <span className="block mt-2 text-destructive">
                  {t('editStructure.deleteCategoryWarning', { count: nodeToDelete.children.length })}
                </span>
              )}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                closeModal('delete');
                setNodeToDelete(null);
              }}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.deleting') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HelpCenterSettingsDrawer
        isOpen={showSettingsDrawer}
        onClose={() => setShowSettingsDrawer(false)}
        helpCenterId={id || null}
        onSuccess={() => {
          fetchData();
        }}
      />
    </AppLayout>
  );
};

export default EditHelpCenterStructurePage;
